import { useTranslation } from 'react-i18next'

import { Button } from '@/components'
import { triggerToast } from '@/components/Toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'

import { useDeleteComment } from '../../hooks/hooks'
import { useThreadStore } from '../../store'

export function DeleteCommentDialog() {
  const { t } = useTranslation()
  const {
    deleteCommentDialogOpen,
    deletingComment,
    setDeleteCommentDialogOpen,
    clearDeletingComment,
    removeCommentFromThread,
  } = useThreadStore()

  const { mutate: deleteComment, isPending: isDeleting } = useDeleteComment(
    deletingComment?.documentVersionId || ''
  )

  const handleConfirm = () => {
    if (!deletingComment) return

    deleteComment(deletingComment.commentId, {
      onSuccess: () => {
        removeCommentFromThread(
          deletingComment.threadId,
          deletingComment.commentId
        )
        triggerToast('success', t('tflViewer.reply.messages.deleteSuccess'))
        setDeleteCommentDialogOpen(false)
        clearDeletingComment()
      },
      onError: () => {
        triggerToast('error', t('tflViewer.reply.messages.errorGeneric'))
      },
    })
  }

  const handleCancel = () => {
    setDeleteCommentDialogOpen(false)
    clearDeletingComment()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel()
    }
  }

  return (
    <Dialog open={deleteCommentDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 text-md font-semibold text-primary-900">
              {t('tflViewer.reply.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('tflViewer.reply.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-md border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={handleCancel}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent !text-white !bg-error-solid disabled:!bg-disabled disabled:!text-disabled"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
